import { ProjectHelper, ServiceList, panelType } from '@bimser/common';
import { BCNotification, findMessage } from '@bimser/components';
import { IBaseAction, IDictionary, MLHelper, isNullOrUndefined } from '@bimser/core';
import { OpeneBAFlowViewer } from 'modules/workflow/components/flowViewer/sagas/helper/openeBAFlowViewer';
import { ProjectPlatform } from 'modules/workflow/entities/IWorkflowItem';
import { call, fork, put, select, take, takeLatest } from 'redux-saga/effects';
import * as commonActionTypes from '../../../../common/actions/actionTypes';
import { WebInterfaceAction } from '../../../../common/entities';
import { DEPLOY_AGENT_READY } from '../../../workflow/actions/actionTypes';
import {
	FlowViewerParameters,
	OpenFlowViewer
} from '../../../workflow/components/flowViewer/sagas/helper/openFlowViewer';
import { setProcessDetailsLoadingAction } from '../../actions';
import * as ActionTypes from '../../actions/actionTypes';
import { TileTypes } from '../../entities/widgets/TileTypes';
import { IProcess } from '../../entities/widgets/model/types/IProcess';
import { getSelectedProcess } from '../../selectors/ProcessesSelectors';

export default function* watchProcessesManager() {
	while (true) {
		const action: IBaseAction<WebInterfaceAction> = yield take(
			(_action: IBaseAction<WebInterfaceAction>) => {
				if (
					_action.type === commonActionTypes.INITIALIZE_PANEL_MANAGER &&
					_action.payload?.panelType === panelType.startupLayout
				) {
					return true;
				}
			}
		);

		yield fork(processDetailsManager, action);
	}
}

const whiteList = [
	ActionTypes.ON_LOAD_PROCESS_DETAILS_TRANSACTION,
	ActionTypes.ON_CLICK_PROCESS_DETAIL_TRANSACTION
];

function* processDetailsManager(action: IBaseAction<WebInterfaceAction>) {
	yield call(getProcessesDetails, action.payload);

	yield takeLatest(
		(dashboardAction: IBaseAction<WebInterfaceAction>) =>
			whiteList.includes(dashboardAction.type) &&
			dashboardAction.payload?.panelId === action.payload?.panelId,
		function* (dashboardAction: IBaseAction<WebInterfaceAction>) {
			switch (dashboardAction.type) {
				case ActionTypes.ON_LOAD_PROCESS_DETAILS_TRANSACTION: {
					yield fork(getProcessesDetails, dashboardAction.payload);
					break;
				}
				case ActionTypes.ON_CLICK_PROCESS_DETAIL_TRANSACTION: {
					yield call(onClickProcessDetail, dashboardAction.payload);
					break;
				}
			}
		}
	);
}

function* waitAgentReady() {
	if (!ProjectHelper.isAgentsReady) {
		yield take((action: IBaseAction<any>) => action.type == DEPLOY_AGENT_READY);
	}
}

function* getProcessesDetails(payload: WebInterfaceAction) {
	yield put(setProcessDetailsLoadingAction(true));

	const selectedProcess: IProcess = yield select(getSelectedProcess);

	if (selectedProcess) {
		yield call(waitAgentReady);
	}
	yield put(setProcessDetailsLoadingAction(false));
}

function* onClickProcessDetail(payload: WebInterfaceAction<IDictionary<any>>) {
	const selectedProcess: IProcess = yield select(getSelectedProcess);
	if (payload.data.fastEventId) {
		yield put(setProcessDetailsLoadingAction(true));
	}

	if (selectedProcess.projectPlatform === ProjectPlatform.Synergy) {
		const parameter: FlowViewerParameters = {
			processId: payload.data.ProcessId,
			requestId: payload.data.RequestId,
			projectVersion: payload.data.PackageVersion,
			projectName: selectedProcess.projectName,
			flowName: selectedProcess.flowName,
			projectCaption: MLHelper.getMLText(selectedProcess.projectCaption),
			mobileFormView: payload.data.MobileFormView,
			fastEventId: payload.data.fastEventId,
			parent: {
				panelGuid: payload.panelId,
				panelType: payload.panelType
			},
			hideHistoryAfterEvent: false
		};

		yield call(OpenFlowViewer, parameter);
	} else if (selectedProcess.projectPlatform === ProjectPlatform.eBA) {
		yield call(OpeneBAFlowViewer, {
			projectName: selectedProcess.projectName,
			flowName: selectedProcess.flowName,
			parent: {
				panelGuid: payload.panelId,
				panelType: payload.panelType
			},
			projectCaption: MLHelper.getMLText(selectedProcess.projectCaption),
			processId: payload.data.ProcessId,
			requestId: payload.data.RequestId
		});
	}
}

let controller: AbortController = null;
export const getProcessesDetailsPromise = async (payload: {
	category: TileTypes;
	process: IProcess;
	skip: number;
	take: number;
}) => {
	if (controller) controller.abort();
	controller = new AbortController();

	const { category, process, skip, take } = payload;

	const processTileTypes = {
		[TileTypes.Waiting]: 0,
		[TileTypes.Notification]: 1,
		[TileTypes.Started]: 2,
		[TileTypes.OnGoing]: 10,
		[TileTypes.Finished]: 11
	};

	if (process) {
		const detailPayload = {
			skip,
			take,
			sorting: 2,
			startDate: '01.01.1753',
			endDate: null,
			processType: !isNullOrUndefined(category) ? processTileTypes[category] : 0
		};

		try {
			const details = await ProjectHelper.RunPromiseFlowService({
				projectName: process.projectName,
				serviceName: 'GetProcessItems',
				requestParameters: detailPayload,
				flowName: process.flowName,
				serviceMethod: 'post',
				requestConfig: {
					controller
				}
			});

			if (details?.data?.queryResult) {
				const result = details.data.queryResult.map((item: any) =>
					isNullOrUndefined(item.id)
						? {
								...item,
								ProcessId: item.ProcessID ?? item.ProcessId,
								id: `${item.RequestId} ${item.ProcessID ?? item.ProcessId}`
						  }
						: {
								...item,
								ProcessId: item.ProcessID ?? item.ProcessId
						  }
				);

				return {
					data: result,
					totalCount: process.count,
					columns: details.data?.structure.columns || []
				};
			}
		} catch (e) {
			if (e.message !== 'canceled') {
				BCNotification.error({ message: findMessage.get('102682') });
			}
		}
	}
};

export const geteBAProcessesDetailsPromise = async (payload: {
	process: IProcess;
	skip: number;
	take: number;
}) => {
	if (controller) controller.abort();
	controller = new AbortController();

	const { process, skip, take } = payload;

	const response = await ServiceList.workflow.workflowManagement.GeteBAWorkflowItems.callPromise(
		{
			skip,
			take,
			projectName: process.projectName
		},
		null,
		{ cache: false }
	);

	if (response?.queryResult) {
		try {
			if (response?.queryResult) {
				const result = response.queryResult.map((item: any) =>
					isNullOrUndefined(item.id)
						? {
								...item,
								ProcessId: item.ProcessID ?? item.ProcessId,
								id: `${item.RequestId} ${item.ProcessID ?? item.ProcessId}`
						  }
						: {
								...item,
								ProcessId: item.ProcessID ?? item.ProcessId
						  }
				);

				return {
					data: result,
					totalCount: process.count,
					columns: response.structure.columns || []
				};
			}
		} catch (e) {
			if (e.message !== 'canceled') {
				BCNotification.error({ message: findMessage.get('102682') });
			}
		}
	}
};
